import { fetchAuthSession } from 'aws-amplify/auth'
import { axiosInstance, axiosManager, cognitoTokenExpiry } from './axiosManager'
import {
  MAX_CONCEPTS,
  CONCEPT_DESCRIPTION_MAX,
  CONCEPT_EXAMPLE_MAX,
  CONCEPT_LABEL_MAX,
} from '../component/session/experiment/experimentConfig'

const experimentUrl = process.env.REACT_APP_HOST_API + '/experiments'
const sessionUrl = process.env.REACT_APP_HOST_API + '/sessions'
const gptUrl = process.env.REACT_APP_HOST_API + '/gpt'

// Whether this backend can publish experiments. The wizard only offers its Experiment switch
// when it can, so a frontend deployed ahead of the backend never shows a switch that fails
// at Publish.
export const getExperimentSupport = async () => {
  try {
    const response = await axiosInstance.get(`${experimentUrl}/support`)
    return Number(response.data?.version) >= 1
  } catch (error) {
    return false
  }
}

// The Authorization header is set once, at page load (AuthContext.checkAuth), and Cognito
// access tokens expire after an hour - easily outlasted by a long authoring session. Ask
// Amplify for the current session (it refreshes an expired token) and use that. Only a
// Cognito token is replaced: a guest's or a password login's own token is left as it is, even
// when this browser still has a Cognito session (a sign-in that fell back to the password
// login keeps one) - swapping it sent the calls as that other account. With no token at all,
// a Cognito session is picked up, as before.
export const refreshSignIn = async () => {
  try {
    const header = axiosInstance.defaults?.headers?.common?.Authorization
    if (header && cognitoTokenExpiry(header) == null) return
    const { tokens } = await fetchAuthSession()
    if (tokens?.accessToken) axiosManager.setAuthToken(tokens.accessToken.toString())
  } catch (error) {
    // Not a Cognito sign-in.
  }
}

const PUBLISH_FAILURES = {
  401: 'Sign in again as an instructor, then press Publish.',
  403: 'Only the instructor who created this draft can publish it.',
  404: 'This draft no longer exists. It may have been deleted from the session list.',
}

// Turns the wizard's draft into an experiment: one session per condition, plus the draft
// itself becoming the experiment's own page. All or nothing on the server.
// Resolves to { ok: true, experiment, conditions } or { ok: false, status, message,
// experimentId? } - experimentId is set when this draft was already published (a double
// click, or a retry after a lost response).
export const publishExperiment = async (payload) => {
  await refreshSignIn()
  try {
    // Creating several sessions in one transaction can take longer than the shared
    // instance's 10s default.
    const response = await axiosInstance.post(experimentUrl, payload, { timeout: 30000 })
    return { ok: true, experiment: response.data.experiment, conditions: response.data.conditions || [] }
  } catch (error) {
    const status = error?.response?.status
    const data = error?.response?.data || {}
    return {
      ok: false,
      status,
      experimentId: data.experimentId ?? null,
      message:
        data.message ||
        PUBLISH_FAILURES[status] ||
        'Could not publish the experiment. Check your connection and press Publish again.',
    }
  }
}

// Session Control for every condition at once (backend: experimentService.controlExperiment).
// action: 'start' (with durationMinutes) | 'pause' | 'resume' | 'reset' | 'end'.
// Resolves to { ok: true, experiment, conditions } or { ok: false, status, message } - status
// is undefined when the server did not answer.
export const controlExperiment = async (experimentId, action, { durationMinutes } = {}) => {
  await refreshSignIn()
  try {
    const response = await axiosInstance.post(
      `${experimentUrl}/${experimentId}/control`,
      { action, durationMinutes },
      { timeout: 30000 }
    )
    return { ok: true, experiment: response.data.experiment, conditions: response.data.conditions || [] }
  } catch (error) {
    const status = error?.response?.status
    return {
      ok: false,
      status,
      message:
        error?.response?.data?.message ||
        (status === 401
          ? 'Sign in again as an instructor, then try again.'
          : 'Could not reach the server. Check your connection and try again.'),
    }
  }
}

// Whether the signed-in account can run the experiment - its creator, or an account the
// creator added - as the server sees it (backend: listInstructors answers only them, 403 to
// everyone else). The list names accounts, not people: an added instructor signed in with
// another of their accounts is refused. true or false (403), or null when that could not be
// told (no answer from the server, a sign-in it did not take, a 5xx).
export const canRunExperiment = async (experimentId) => {
  await refreshSignIn()
  try {
    await axiosInstance.get(`${experimentUrl}/${experimentId}/instructors`)
    return true
  } catch (error) {
    return error?.response?.status === 403 ? false : null
  }
}

// A student opening the experiment's link, or one of its conditions' links (`fromSessionId`):
// the condition they work in, joining them the first time (backend: enterExperiment).
// Resolves to { ok: true, sessionId, sessionType, assigned, user } or { ok: false, status,
// message, staff } - status is undefined when the server did not answer. `refresh`: whether
// to refresh a Cognito sign-in first (see refreshSignIn).
export const enterExperiment = async (experimentId, fromSessionId, { refresh = true } = {}) => {
  if (refresh) await refreshSignIn()
  try {
    const response = await axiosInstance.post(
      `${experimentUrl}/${experimentId}/enter`,
      { fromSessionId },
      { timeout: 20000 }
    )
    return { ok: true, ...response.data }
  } catch (error) {
    const data = error?.response?.data || {}
    return { ok: false, status: error?.response?.status, message: data.message || '', staff: Boolean(data.staff) }
  }
}

// ---- Offloading (backend: services/offloadingService.js) ----

// The signed-in student's panel in a condition session: { show, concepts: [{ id, label }] },
// the concepts they asked the AI about. show is false unless their condition shows the panel.
// Throws when the server did not answer. `refresh`: refresh a Cognito sign-in first (students
// with a uid; a guest's token must not be swapped for a Cognito session left in the browser).
export const getOffloadingPanel = async (sessionId, { refresh = false } = {}) => {
  if (refresh) await refreshSignIn()
  const response = await axiosInstance.get(`${experimentUrl}/offloading/${sessionId}`)
  return {
    show: Boolean(response.data?.show),
    concepts: Array.isArray(response.data?.concepts) ? response.data.concepts : [],
  }
}

// Per condition and student, what they offloaded:
// { concepts, conditions: [{ key, sessionId, mode, students: { [userId]: { offloaded, byConcept,
// analyzed, offloadedTurns } } }] }, plus `exchanges` (every labeled exchange, for the export)
// when asked for. Throws when it could not be loaded.
export const getOffloadingSummary = async (experimentId, { exchanges = false } = {}) => {
  await refreshSignIn()
  const response = await axiosInstance.get(`${experimentUrl}/${experimentId}/offloading`, {
    params: exchanges ? { detail: 'exchanges' } : undefined,
    timeout: 30000,
  })
  if (!Array.isArray(response.data?.conditions)) throw new Error('Unexpected response while loading the offloading summary')
  return response.data
}

// The experiment's answer key, for the instructor's Presenter: { exerciseId, code } - the
// reference solution of the experiment's exercise. Only its creator and added instructors get
// it, and never with the exercise students load. null when this account may not see it (403),
// when the exercise has none (404) or when the reply has no code; throws for anything else
// (no answer from the server, a 5xx), for the caller to try again.
export const getAnswerKey = async (experimentId) => {
  await refreshSignIn()
  try {
    const response = await axiosInstance.get(`${experimentUrl}/${experimentId}/answer-key`)
    const code = response.data?.code
    if (typeof code !== 'string' || !code.trim()) return null
    return { exerciseId: response.data?.exerciseId ?? null, code }
  } catch (error) {
    const status = error?.response?.status
    if (status === 403 || status === 404) return null
    throw error
  }
}

// The condition sessions an experiment page lists. Unlike sessionService.getSessionsByIds,
// which resolves to [] on any failure, this rejects, so "could not load" is never shown as
// "the sessions are gone".
export const getConditionSessions = async (ids) => {
  const response = await axiosInstance.post(`${sessionUrl}/ids`, { ids })
  if (!Array.isArray(response.data)) throw new Error('Unexpected response while loading the condition sessions')
  return response.data
}

// ---- The classroom study's note about a changed AI access (backend: studyStage2.transitionNotice) ----

// The note the signed-in student's "Before you start" instructions open with in a condition
// session (workspace/study/StudyInstructions.jsx): 'ai-removed' or 'ai-later' when the server's
// record says their AI access changed in this exercise, else null. null as well for anything that
// goes wrong - no answer within NOTICE_TIMEOUT_MS, a sign-in the server did not take, a reply of
// another shape: the instructions never wait for it, and without it they read as before.
const NOTICE_TIMEOUT_MS = 5000

export const fetchTransitionNotice = async (sessionId) => {
  try {
    const response = await axiosInstance.get(`${experimentUrl}/notice/${sessionId}`, { timeout: NOTICE_TIMEOUT_MS })
    const notice = response?.data?.notice
    return typeof notice === 'string' && notice ? notice : null
  } catch (error) {
    return null
  }
}

// ---- AI draft of the concept list (backend: services/authoringService.js, Azure gpt-4o) ----

const clip = (value, max) => String(value ?? '').trim().slice(0, max)

// Resolves to [{ label, description, example }] (possibly empty); throws when the AI call fails.
export const draftConcepts = async ({ task, type, template, testList }) => {
  const response = await axiosInstance.post(
    `${gptUrl}/concepts`,
    { task, language: type, template, tests: (testList || []).slice(0, 8) },
    { timeout: 60000 }
  )
  return (Array.isArray(response.data?.concepts) ? response.data.concepts : [])
    .map((concept) => ({
      label: clip(concept?.label, CONCEPT_LABEL_MAX),
      description: clip(concept?.description, CONCEPT_DESCRIPTION_MAX),
      example: clip(concept?.example, CONCEPT_EXAMPLE_MAX),
    }))
    .filter((concept) => concept.label)
    .slice(0, MAX_CONCEPTS)
}
